// ActivityRelationshipChip.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useNavigation } from 'context/NavigationContext';
import { useTheme } from 'context/ThemeContext';

// Hooks
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';

// Services
import { getNode } from 'services/nodes/NodeReadService';
import { getNodeMediaDownloadName } from 'services/nodes/nodeMediaDownload';
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// Utilities
import { SectionType } from 'utilities/Enums';
import { isPdfMimeType } from 'utilities/fileMime';
import {
  isNodeMediaTypeKey,
  normalizeNodeMediaTypeKey,
} from 'node/highlight/NodeMediaHighlight';

// CSS
import './ActivityRelationshipChip.css';

// Icons
import DocumentIcon from 'svg/DocumentIcon';
import PdfIcon from 'svg/PdfIcon';
import PresentationIcon from 'svg/PresentationIcon';
import SpreadsheetIcon from 'svg/SpreadsheetIcon';

const pickCandidateUrls = (related) => (
  [
    // Prefer the original object — derivative thumbs may not exist yet right
    // after upload (Cloud Function generates them asynchronously).
    related?.url,
    related?.storagePath,
    related?.url_feed,
    related?.url_grid,
    related?.url_thumb,
    related?.coverUrl,
    related?.relatedNodeUrl,
    related?.relatedNodeUrlGrid,
    related?.relatedNodeUrlThumb,
    related?.relatedNodeCoverUrl,
  ]
    .filter((value, index, arr) => (
      typeof value === 'string'
      && value.trim()
      && arr.indexOf(value) === index
    ))
);

const cleanString = (value) => String(value || '').trim();

/**
 * @param {object|null|undefined} related
 * @returns {{ filename: string, extension: string, mimeType: string }}
 */
function pickRelatedFileMeta(related) {
  const filename = cleanString(
    related?.filename
    || related?.fileName
    || related?.data_document?.filename
    || related?.data_document?.fileName
    || related?.data_file?.filename
    || related?.data_file?.fileName
  );
  const extension = cleanString(
    related?.extension
    || related?.data_document?.extension
    || related?.data_file?.extension
    || (filename.includes('.') ? filename.split('.').pop() : '')
  ).replace(/^\./, '').toLowerCase();
  const mimeType = cleanString(
    related?.mimeType
    || related?.data_document?.mimeType
    || related?.data_document?.contentType
    || related?.data_file?.mimeType
    || related?.data_file?.contentType
  ).toLowerCase();

  return { filename, extension, mimeType };
}

/**
 * @param {{ extension?: string, mimeType?: string }} meta
 * @returns {React.ComponentType<{ width?: number, height?: number }>}
 */
function resolveRelatedFileIcon({ extension = '', mimeType = '' } = {}) {
  const ext = String(extension || '').toLowerCase();
  const mime = String(mimeType || '').toLowerCase();

  if (ext === 'pdf' || isPdfMimeType(mime)) {
    return PdfIcon;
  }

  if (
    ['ppt', 'pptx', 'pps', 'ppsx', 'odp', 'key'].includes(ext)
    || mime.startsWith('application/vnd.ms-powerpoint')
    || mime.includes('presentation')
  ) {
    return PresentationIcon;
  }

  if (
    ['xls', 'xlsx', 'csv', 'tsv', 'ods', 'xlsm', 'numbers'].includes(ext)
    || mime.includes('spreadsheet')
    || mime.includes('excel')
  ) {
    return SpreadsheetIcon;
  }

  return DocumentIcon;
}

/**
 * @param {object} event
 * @returns {object|null}
 */
export function resolveRelatedNodeFromActivityEvent(event) {
  const change = Array.isArray(event?.changes) ? event.changes[0] : null;
  const related = event?.relatedNode || null;
  const key = String(
    related?.key || change?.relatedNodeKey || ''
  ).trim();
  if (!key) return null;

  const filename = cleanString(
    related?.filename
    || change?.relatedNodeFilename
  ) || null;
  const extension = cleanString(
    related?.extension
    || change?.relatedNodeExtension
  ).replace(/^\./, '') || null;
  const mimeType = cleanString(
    related?.mimeType
    || change?.relatedNodeMimeType
  ) || null;

  return {
    key,
    title: String(related?.title || change?.relatedNodeTitle || 'Related item').trim() || 'Related item',
    type: String(related?.type || change?.relatedNodeType || '').trim(),
    changeKind: String(change?.changeKind || '').trim(),
    url: String(related?.url || change?.relatedNodeUrl || '').trim() || null,
    url_thumb: String(related?.url_thumb || change?.relatedNodeUrlThumb || '').trim() || null,
    url_grid: String(related?.url_grid || change?.relatedNodeUrlGrid || '').trim() || null,
    coverUrl: String(related?.coverUrl || change?.relatedNodeCoverUrl || '').trim() || null,
    storagePath: String(related?.storagePath || change?.relatedNodeStoragePath || '').trim() || null,
    filename,
    extension,
    mimeType,
  };
}

/**
 * ActivityRelationshipChip
 *
 * Bullet / thumbnail / file row linking to the related node.
 *
 * @param {object} event
 * @param {'default'|'grid'} [variant] - Natural-height chip, or square grid thumb.
 * @returns {ReactNode}
 */
const ActivityRelationshipChip = React.memo(({
  event,
  variant = 'default',
}) => {
  const { theme } = useTheme();
  const { currentSection } = useNavigation();
  const { openNode } = useNodeOpenIntent();

  const relatedSeed = useMemo(
    () => resolveRelatedNodeFromActivityEvent(event),
    [event]
  );

  const [related, setRelated] = useState(relatedSeed);
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    setRelated(relatedSeed);
    setImageUrl(null);
  }, [relatedSeed]);

  // Older events may lack media URLs / file meta — hydrate from the live node once.
  useEffect(() => {
    if (!relatedSeed?.key) return undefined;
    if (!isNodeMediaTypeKey(relatedSeed.type)) return undefined;

    const mediaType = normalizeNodeMediaTypeKey(relatedSeed.type);
    const hasPhotoUrls = pickCandidateUrls(relatedSeed).length > 0 || relatedSeed.storagePath;
    const hasFileMeta = Boolean(relatedSeed.filename || relatedSeed.extension || relatedSeed.mimeType);

    if (mediaType === 'PHOTO' || mediaType === 'VIDEO') {
      if (hasPhotoUrls) return undefined;
    } else if (mediaType === 'DOCUMENT' || mediaType === 'FILE') {
      if (hasFileMeta) return undefined;
    } else {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const loaded = await getNode(relatedSeed.key);
        if (cancelled || !loaded?.key) return;
        const fileMeta = pickRelatedFileMeta(loaded);
        setRelated((prev) => ({
          ...prev,
          title: String(loaded.title || prev.title || 'Related item').trim() || prev.title,
          type: String(loaded.type || prev.type || '').trim() || prev.type,
          url: loaded.url || prev.url,
          url_thumb: loaded.url_thumb || prev.url_thumb,
          url_grid: loaded.url_grid || prev.url_grid,
          coverUrl: loaded.coverUrl || prev.coverUrl,
          storagePath: loaded.storagePath || prev.storagePath,
          filename: fileMeta.filename || prev.filename,
          extension: fileMeta.extension || prev.extension,
          mimeType: fileMeta.mimeType || prev.mimeType,
          data_document: loaded.data_document || prev.data_document,
          data_file: loaded.data_file || prev.data_file,
          fileName: loaded.fileName || prev.fileName,
        }));
      } catch {
        // Keep the title fallback.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [relatedSeed]);

  const candidateUrls = useMemo(() => pickCandidateUrls(related), [related]);
  const mediaType = normalizeNodeMediaTypeKey(related?.type);
  const showThumbnail = mediaType === 'PHOTO' || mediaType === 'VIDEO';
  const showFileRow = mediaType === 'DOCUMENT' || mediaType === 'FILE';

  const fileMeta = useMemo(() => pickRelatedFileMeta(related), [related]);
  const FileIcon = useMemo(
    () => resolveRelatedFileIcon(fileMeta),
    [fileMeta]
  );
  const displayLabel = useMemo(() => {
    if (!related) return '';
    if (!showFileRow) return related.title;
    return getNodeMediaDownloadName({
      type: related.type,
      title: related.title,
      filename: related.filename,
      fileName: related.fileName || related.filename,
      extension: related.extension,
      data_document: related.data_document || {
        filename: related.filename,
        extension: related.extension,
        mimeType: related.mimeType,
      },
      data_file: related.data_file || {
        filename: related.filename,
        extension: related.extension,
        mimeType: related.mimeType,
      },
    });
  }, [related, showFileRow]);

  useEffect(() => {
    if (!showThumbnail || !related?.key) {
      setImageUrl(null);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      const tryResolve = async (rawUrl, storagePath = null) => {
        const url = await resolveImageUrl({
          nodeKey: related.key,
          url: rawUrl,
          storagePath,
        });
        return url || null;
      };

      for (const rawUrl of candidateUrls) {
        const url = await tryResolve(rawUrl, null);
        if (cancelled) return;
        if (url) {
          setImageUrl(url);
          return;
        }
      }

      if (related.storagePath) {
        const fallback = await tryResolve(null, related.storagePath);
        if (cancelled) return;
        if (fallback) {
          setImageUrl(fallback);
          return;
        }
      }

      // Derivatives may be listed before they exist; reload the live node and
      // prefer its original storage object for an immediate preview.
      try {
        const loaded = await getNode(related.key);
        if (cancelled || !loaded?.key) {
          setImageUrl(null);
          return;
        }

        setRelated((prev) => ({
          ...prev,
          url: loaded.url || prev.url,
          url_thumb: loaded.url_thumb || prev.url_thumb,
          url_grid: loaded.url_grid || prev.url_grid,
          coverUrl: loaded.coverUrl || prev.coverUrl,
          storagePath: loaded.storagePath || prev.storagePath,
        }));

        const loadedCandidates = pickCandidateUrls(loaded);
        for (const rawUrl of loadedCandidates) {
          const url = await tryResolve(rawUrl, null);
          if (cancelled) return;
          if (url) {
            setImageUrl(url);
            return;
          }
        }

        if (loaded.storagePath) {
          const fallback = await tryResolve(null, loaded.storagePath);
          if (cancelled) return;
          setImageUrl(fallback || null);
          return;
        }
      } catch {
        // Keep placeholder.
      }

      if (!cancelled) setImageUrl(null);
    })();

    return () => {
      cancelled = true;
    };
  }, [candidateUrls, related?.key, related?.storagePath, showThumbnail]);

  if (!related) return null;

  return (
    <button
      className={[
        'activity-relationship-chip-container',
        showThumbnail ? 'has-thumbnail' : '',
        showFileRow ? 'has-file' : '',
        variant === 'grid' && showThumbnail ? 'is-grid' : '',
      ].filter(Boolean).join(' ')}
      type="button"
      title={displayLabel}
      aria-label={displayLabel}
      style={{
        color: theme.foreground,
      }}
      onClick={() => openNode({
        key: related.key,
        title: related.title,
        type: related.type,
        url: related.url,
        url_thumb: related.url_thumb,
        url_grid: related.url_grid,
        coverUrl: related.coverUrl,
        storagePath: related.storagePath,
      }, currentSection === SectionType.NODE
        ? { preferPeek: false }
        : undefined)}
    >
      {showThumbnail ? (
        <span className="activity-relationship-chip-thumb">
          {imageUrl ? (
            <img
              alt=""
              className="activity-relationship-chip-thumb-image"
              src={imageUrl}
              draggable={false}
              onError={() => setImageUrl(null)}
            />
          ) : (
            <span
              className="activity-relationship-chip-thumb-placeholder"
              style={{
                backgroundColor: theme.backgroundMuted,
              }}
            />
          )}
        </span>
      ) : showFileRow ? (
        <span className="activity-relationship-chip-file">
          <span className="activity-relationship-chip-file-icon" aria-hidden="true">
            <FileIcon width={18} height={18} />
          </span>
          <span className="activity-relationship-chip-title">{displayLabel}</span>
        </span>
      ) : (
        <span className="activity-relationship-chip-title">{displayLabel}</span>
      )}
    </button>
  );
});

ActivityRelationshipChip.displayName = 'ActivityRelationshipChip';

export default ActivityRelationshipChip;
